// Constants
export const STORAGE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const STORAGE_KEY_PREFIX = 'salla_auth_verification_';

/**
 * Auth data structure stored in localStorage
 */
interface StoredAuthData {
  data: {
    type: 'mobile' | 'email';
    phone?: string;
    country_code?: string;
    country_key?: string;
    email?: string;
  };
  timestamp: number;
  storeId: string;
}

/**
 * Validate stored auth data structure
 */
function isValidStoredData(obj: any): obj is StoredAuthData {
  return (
    obj &&
    typeof obj === 'object' &&
    obj.data &&
    typeof obj.data === 'object' &&
    typeof obj.timestamp === 'number' &&
    typeof obj.storeId === 'string' &&
    (obj.data.type === 'mobile' || obj.data.type === 'email')
  );
}

function getStorageKey(storeId: string): string {
  return `${STORAGE_KEY_PREFIX}${storeId}`;
}

/**
 * Save auth data to localStorage
 * localStorage persists even if iframe is recreated or refreshed
 */
export function saveAuthData(data: any, storeId: string, logger?: any): boolean {
  try {
    const storageData: StoredAuthData = {
      data,
      timestamp: Date.now(),
      storeId,
    };
    
    const storageKey = getStorageKey(storeId);
    localStorage.setItem(storageKey, JSON.stringify(storageData));
    
    logger?.log?.('authStorage: Saved data to localStorage for store', storeId);
    return true;
  } catch (e) {
    logger?.warn?.('authStorage: Failed to save to localStorage', e);
    return false;
  }
}

/**
 * Recover auth data from localStorage with validation
 * Returns null if data is invalid, expired, or belongs to different store
 */
export function recoverAuthData(storeId: string, logger?: any): any | null {
  try {
    const storageKey = getStorageKey(storeId);
    const savedData = localStorage.getItem(storageKey);
    
    if (!savedData) {
      logger?.log?.('authStorage: No data found in localStorage');
      return null;
    }

    const parsed = JSON.parse(savedData);
    if (!isValidStoredData(parsed)) {
      logger?.warn?.('authStorage: Invalid data structure, clearing');
      localStorage.removeItem(storageKey);
      return null;
    }

    // Validate store ID match
    if (parsed.storeId !== storeId) {
      logger?.warn?.('authStorage: Store ID mismatch, clearing');
      localStorage.removeItem(storageKey);
      return null;
    }

    // Validate timestamp (not expired)
    const age = Date.now() - parsed.timestamp;
    if (age > STORAGE_TTL_MS) {
      logger?.warn?.('authStorage: Data expired (age: ' + Math.round(age/1000) + 's), clearing');
      localStorage.removeItem(storageKey);
      return null;
    }

    logger?.log?.('authStorage: Successfully recovered data from localStorage (age: ' + Math.round(age/1000) + 's)');
    return parsed.data;
  } catch (e) {
    logger?.error?.('authStorage: Failed to recover from localStorage', e);
    return null;
  }
}

/**
 * Clear auth data for specific store from localStorage
 */
export function clearAuthData(storeId: string, logger?: any): void {
  try {
    const storageKey = getStorageKey(storeId);
    localStorage.removeItem(storageKey);
    logger?.log?.('authStorage: Cleared data from localStorage for store', storeId);
  } catch (e) {
    logger?.warn?.('authStorage: Failed to clear localStorage', e);
  }
}
